import { Satellite, Battery, MapPin, FileText, CreditCard, Car, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const WHATSAPP_NUMBER = '5591984849479';

const plans = [
  {
    name: 'Diária',
    price: 120,
    period: 'por dia',
    description: 'Ideal para eventos de curta duração',
    featured: false
  },
  {
    name: 'Semanal',
    price: 100,
    period: 'por dia',
    description: 'Perfeito para viagens de uma semana',
    featured: false
  },
  {
    name: 'Quinzenal',
    price: 80,
    period: 'por dia',
    description: 'Ótimo custo-benefício para uso prolongado',
    featured: true
  },
  {
    name: 'Mensal',
    price: 65,
    period: 'por dia',
    description: 'Melhor preço para uso contínuo',
    featured: false
  }
];

const features = [
  {
    icon: MapPin,
    title: 'Dispositivo Rastreado',
    description: 'Segurança total com rastreamento em tempo real'
  },
  {
    icon: Battery,
    title: 'Powerbank Incluso',
    description: '60.000mAh com 6-8 horas de autonomia'
  },
  {
    icon: Satellite,
    title: 'Plano Ilimitado',
    description: 'Funciona em qualquer lugar do Brasil'
  },
  {
    icon: FileText,
    title: 'Nota Fiscal',
    description: 'Emitimos NF para todos os aluguéis'
  },
  {
    icon: CreditCard,
    title: 'Aceita Cartão',
    description: 'Pagamento facilitado em cartão'
  }
];

export default function StarlinkPage() {
  const handleWhatsApp = (plan = null) => {
    let message = '🛰️ *Interesse em Aluguel Starlink Mini*\n\n';
    if (plan) {
      message += `Plano: *${plan.name}* - R$ ${plan.price},00/dia\n\n`;
    }
    message += 'Gostaria de mais informações sobre o aluguel.';
    
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen" data-testid="starlink-page">
      {/* Hero */}
      <section className="starlink-hero py-16 md:py-24 text-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm font-medium">
                <Satellite className="w-4 h-4" />
                Aluguel com Contrato
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-secondary font-bold tracking-tight">
                Starlink <span className="text-blue-400">Mini</span>
              </h1>
              
              <p className="text-lg text-slate-300 leading-relaxed max-w-lg">
                Internet via satélite de alta velocidade para onde você estiver. 
                Ideal para eventos, viagens, áreas remotas ou backup de conexão.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => handleWhatsApp()}
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full px-8 py-6 font-bold shadow-lg shadow-green-500/20 transition-transform hover:scale-105"
                  data-testid="starlink-whatsapp-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Falar no WhatsApp
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden bg-slate-800/50 border border-slate-700/50 backdrop-blur">
                <img 
                  src="https://lojabrmetaverso.com.br/cdn/shop/files/KitStarlinkMini-InternetviaSatelite.png?v=1734644277"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-blue-600 text-white px-6 py-3 rounded-2xl shadow-xl">
                <p className="text-sm font-medium">A partir de</p>
                <p className="text-2xl font-bold font-secondary">R$ 65/dia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-secondary font-bold text-slate-900 dark:text-white mb-4">
              Escolha seu Plano
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Quanto maior o período, menor o valor diário. Pagamento antecipado, retirada 1 dia antes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`plan-card ${plan.featured ? 'plan-card-featured' : ''}`}
                data-testid={`plan-card-${plan.name.toLowerCase()}`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      MELHOR CUSTO
                    </span>
                  </div>
                )}
                
                <h3 className="font-secondary font-bold text-xl text-slate-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                
                <div className="mb-4">
                  <span className="text-4xl font-bold text-blue-600 dark:text-blue-400 font-secondary">
                    R$ {plan.price}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-sm">
                    /{plan.period}
                  </span>
                </div>
                
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                  {plan.description}
                </p>
                
                <Button
                  onClick={() => handleWhatsApp(plan)}
                  className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white rounded-lg"
                  data-testid={`select-plan-${plan.name.toLowerCase()}`}
                >
                  Solicitar
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-secondary font-bold text-slate-900 dark:text-white mb-4">
              O que está incluso
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
                data-testid={`starlink-feature-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Plans */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <Car className="w-7 h-7 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <h3 className="text-2xl font-secondary font-bold text-slate-900 dark:text-white">
                  Planos Veiculares
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Instalação em veículos para uso contínuo
                </p>
              </div>
            </div>
            
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Para planos veiculares, entre em contato para mais detalhes sobre instalação, 
              configuração e valores personalizados para seu caso.
            </p>

            <Button
              onClick={() => handleWhatsApp()}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6"
              data-testid="vehicle-contact-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Entrar em Contato
            </Button>
          </div>
        </div>
      </section>

      {/* Info Box */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-blue-600 text-white rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-secondary font-bold mb-6">
              Informações Importantes
            </h3>
            <ul className="space-y-3">
              {[
                'Dispositivo rastreado para sua segurança',
                'Acompanha powerbanks de 60.000mAh com duração de 6-8 horas fora da tomada',
                'Plano ilimitado que funciona em qualquer lugar',
                'Pagamento antecipado, retirada 1 dia antes',
                'Emitimos Nota Fiscal e aceitamos cartão'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
